import { DateTime } from 'luxon'
import type { TelehealthAudioSource } from './components/telehealth-toggle'

export function formatTimePassedText(date: DateTime) {
  const now = DateTime.now()

  if (now.toISODate() === date.toLocal().toISODate()) {
    return `Today at ${date.toLocaleString(DateTime.TIME_SIMPLE)}`
  } else if (now.minus({ day: 1 }).toISODate() === date.toLocal().toISODate()) {
    return `Yesterday at ${date.toLocaleString(DateTime.TIME_SIMPLE)}`
  } else {
    return `${date.toLocal().toFormat('LLL d')} at ${date.toLocaleString(
      DateTime.TIME_SIMPLE
    )}`
  }
}

export function formatDuration(startedAt: string, completedAt: string) {
  const start = DateTime.fromISO(startedAt).set({ second: 0, millisecond: 0 })
  const end = DateTime.fromISO(completedAt).set({ second: 0, millisecond: 0 })
  const duration = end
    .diff(start, ['hours', 'minutes'])
    .shiftTo('hours', 'minutes')

  const hours = Math.floor(duration.hours)
  const minutes = Math.round(duration.minutes)

  if (hours === 0 && minutes === 0) {
    return '0 min'
  }

  const hoursDisplay = hours > 0 ? `${hours} hr` : ''
  const minutesDisplay = minutes > 0 ? `${minutes} min` : ''

  return [hoursDisplay, minutesDisplay].filter(Boolean).join(' ')
}

export function formatDurationFromSeconds(seconds: number) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)

  if (hours === 0 && minutes === 0) {
    return '0 min'
  }

  const hoursDisplay = hours > 0 ? `${hours} hr` : ''
  const minutesDisplay = minutes > 0 ? `${minutes} min` : ''

  return [hoursDisplay, minutesDisplay].filter(Boolean).join(' ')
}

export const formatTimeForSessionTimer = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const remainingMinutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours < 10 ? '0' : ''}${hours}:${
      remainingMinutes < 10 ? '0' : ''
    }${remainingMinutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  return `${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}:${
    remainingSeconds < 10 ? '0' : ''
  }${remainingSeconds}`
}

export const shouldShareSystemAudio = ({
  isWindows,
  isChromium,
  isTelehealth,
  isUsingHeadphones,
  isMobile,
  telehealthAudioSource,
  qualityToggle,
  isTelehealthSourceRequiredEnabled = false
}: {
  isWindows: boolean
  isChromium: boolean
  isTelehealth: boolean
  isUsingHeadphones: boolean
  isMobile: boolean
  telehealthAudioSource: TelehealthAudioSource | null
  qualityToggle: boolean | null
  isTelehealthSourceRequiredEnabled: boolean
}) => {
  // Never share audio if not in telehealth or on mobile or telehealthAudioSource is null
  if (
    !isTelehealth ||
    isMobile ||
    !!(telehealthAudioSource === null && isTelehealthSourceRequiredEnabled)
  ) {
    return false
  }

  // Always share audio when using headphones (except Mac desktop app)
  if (isUsingHeadphones) {
    // Mac desktop app with headphones should not share audio
    if (!isWindows && telehealthAudioSource === 'desktop') {
      return false
    }
    return true
  }

  if (telehealthAudioSource === 'browser') {
    return qualityToggle === true
  }

  if (telehealthAudioSource === 'desktop') {
    return isWindows && qualityToggle === true
  }

  return false
}
